import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import Tag from '../shared/Tag';
import { eachDateInRange } from '../../utils/leaveDays';
import Avatar from '../shared/Avatar';

// ── THE LEAVE CALENDAR ────────────────────────────────────────────────────────
//
// Who is off, and when, for everybody. El's item 29: "Calendar should be
// viewable by all staff", because it is what people need before booking their
// own leave or a meeting. It lived as a tab on the Holidays page until that
// page became the managers' approvals view (16 September 2026); the calendar
// moved here so it could stay open to every internal role.
//
// The server decides what a caller gets from /leave-requests: for somebody
// with no reports that is approved leave only, with the reason removed. This
// page draws approved leave whatever comes back, so it shows the same thing
// to a manager and to the newest starter.

// Leave type colour mapping for calendar
const LEAVE_TYPE_COLORS = {
  annual:          '#3B82F6', // blue
  sick:            '#EF4444', // red
  wellness:        '#8B5CF6', // purple
  benefits_leave:  '#F59E0B', // amber
  tenure_annual:   '#10B981', // emerald
  carry_over:      '#6366F1', // indigo
  company_training:'#0EA5E9', // sky
  toil:            '#14B8A6', // teal
  other:           '#9CA3AF', // gray
};
const LEAVE_TYPE_LABELS = {
  annual: 'Annual Leave', sick: 'Sick', wellness: 'Wellness Day',
  benefits_leave: 'Benefits', tenure_annual: 'Tenure Leave',
  carry_over: 'Carry Over', company_training: 'Training', toil: 'TOIL', other: 'Other',
};

// ── Calendar helpers ──────────────────────────────────────────────────────────
export function CalendarView({ requests }) {
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const approved = requests.filter(r => r.status === 'approved');

  const year  = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build a map: "YYYY-MM-DD" -> [{name, color, type}]
  const dayMap = {};
  approved.forEach(r => {
    for (const key of eachDateInRange(r.start_date, r.end_date)) {
      if (!dayMap[key]) dayMap[key] = [];
      dayMap[key].push({ name: r.person_name, color: LEAVE_TYPE_COLORS[r.leave_type] || '#9CA3AF', type: r.leave_type });
    }
  });

  const prevMonth = () => setCalMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCalMonth(new Date(year, month + 1, 1));

  const monthLabel = calMonth.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  const startOffset = (firstDay + 6) % 7;
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  return (
    <div className="bg-white border border-gray-200 rounded mb-3">
      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap px-3 pt-2 pb-1 border-b border-gray-100">
        {Object.entries(LEAVE_TYPE_LABELS).map(([k, label]) => (
          <span key={k} className="flex items-center gap-1 text-[7px] text-gray-500">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: LEAVE_TYPE_COLORS[k] }} />{label}
          </span>
        ))}
      </div>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
        <button onClick={prevMonth} className="px-2 py-0.5 text-[10px] font-semibold text-gray-600 hover:text-navy border border-gray-200 rounded">‹ Prev</button>
        <div className="text-[11px] font-extrabold text-navy">{monthLabel}</div>
        <button onClick={nextMonth} className="px-2 py-0.5 text-[10px] font-semibold text-gray-600 hover:text-navy border border-gray-200 rounded">Next ›</button>
      </div>
      {/* Scrollable calendar wrapper */}
      <div className="overflow-x-auto">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-100 min-w-[560px]">
        {days.map(d => (
          <div key={d} className="px-1 py-1 text-center text-[8px] font-bold text-gray-400 uppercase">{d}</div>
        ))}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7 min-w-[560px]">
        {Array.from({ length: startOffset }).map((_, i) => (
          <div key={`e${i}`} className="border-r border-b border-gray-200 min-h-[60px]" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const entries = dayMap[key] || [];
          const isToday = key === new Date().toISOString().slice(0, 10);
          const col = (startOffset + i) % 7;
          const isWeekend = col === 5 || col === 6;
          return (
            <div key={key} className={`border-r border-b border-gray-200 min-h-[60px] p-1 ${isWeekend ? 'bg-gray-50/70' : ''} ${isToday ? 'ring-2 ring-inset ring-navy' : ''}`}>
              <div className={`text-[9px] font-bold mb-0.5 w-5 h-5 flex items-center justify-center rounded-full ${isToday ? 'bg-navy text-white' : 'text-gray-500'}`}>
                {day}
              </div>
              <div className="space-y-0.5">
                {entries.slice(0, 4).map((e, j) => (
                  <div key={j} className="flex items-center gap-0.5 group relative" title={`${e.name} — ${LEAVE_TYPE_LABELS[e.type] || e.type}`}>
                    <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: e.color }} />
                    <span className="text-[7px] text-gray-700 truncate leading-tight font-medium">{e.name}</span>
                  </div>
                ))}
                {entries.length > 4 && (
                  <div className="relative group">
                    <div className="text-[7px] text-gray-400 cursor-default">+{entries.length - 4} more</div>
                    <div className="hidden group-hover:block absolute left-0 top-full z-20 mt-0.5 min-w-[150px] bg-white border border-gray-200 rounded shadow-lg p-1.5 space-y-0.5">
                      <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">{entries.length} off on {day} {calMonth.toLocaleDateString('en-GB', { month: 'short' })}</div>
                      {entries.map((e, j) => (
                        <div key={j} className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: e.color }} />
                          <span className="text-[8px] text-gray-700 font-medium whitespace-nowrap">{e.name}</span>
                          <span className="text-[7px] text-gray-400 whitespace-nowrap">{LEAVE_TYPE_LABELS[e.type] || e.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </div>{/* end overflow-x-auto */}
      {/* Legend */}
      <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-[8px] text-gray-400">
        Showing approved leave only. Coloured dots represent individual team members.
      </div>
    </div>
  );
}

export default function LeaveCalendarPage() {
  const [requests, setRequests] = useState([]);
  useEffect(() => { api.get('leave-requests').then(setRequests).catch(() => setRequests([])); }, []);

  const today = new Date().toISOString().split('T')[0];
  const awayToday = requests.filter(r => r.status === 'approved' && r.start_date <= today && r.end_date >= today);

  return (
    <div className="fade-in">
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div>
          <h2 className="text-base font-extrabold text-navy">Leave Calendar</h2>
          <div className="text-[9px] text-gray-500">Approved leave across the organisation</div>
        </div>
        <span className="text-[10px] text-gray-500 italic">
          Leave is requested from My Space → My Info, where your own allowance and balance are.
        </span>
      </div>

      {awayToday.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
          <h4 className="text-[9px] font-bold text-blue-700 mb-1">Away Today — Organisation</h4>
          <div className="flex flex-wrap gap-2">{awayToday.map(r => (
            <div key={r.id} className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-blue-200">
              <Avatar personId={r.person_id} className="w-4 h-4 rounded-full flex items-center justify-center text-[6px] font-bold text-white" style={{ background: r.avatar_color || '#3B82F6' }}>{r.person_name?.charAt(0)}</Avatar>
              <span className="text-[9px] font-semibold">{r.person_name}</span>
              <Tag status={r.leave_type} />
            </div>
          ))}</div>
        </div>
      )}

      <CalendarView requests={requests} />
    </div>
  );
}
